"use client"

import { useRef } from "react"
import { Icon } from "@/shared/ui/icon"
import { AtlasAgentRun, AtlasUserMessage, type AtlasChatStep } from "./AtlasChatMockup"
import { AtlasInlineOrb, AtlasNodeOrb } from "./AtlasThinkingOrb"
import { useAtlasReveal } from "./useAtlasReveal"

const companyContextRevealTargets = [
  { selector: ".company-context-copy", y: 34 },
  { selector: ".company-context-card", y: 26, scale: 0.96, stagger: 60 },
  { selector: ".specialist-step", y: 30, scale: 0.94, stagger: 80 },
  { selector: ".specialists-run", y: 32, scale: 0.97 },
] as const

const contextItems = [
  {
    title: "Регламенты",
    text: "порядок согласований, роли, зоны ответственности",
    icon: "clipboard-list-linear",
  },
  {
    title: "Рабочие источники",
    text: "CRM, задачи, события и разрешённые сигналы команды",
    icon: "database-linear",
  },
  {
    title: "Проекты",
    text: "активные задачи, статусы, сроки и блокеры",
    icon: "widget-2-bold-duotone",
  },
  {
    title: "Терминология",
    text: "внутренние названия, продукты и клиентские сегменты",
    icon: "book-2-bold-duotone",
  },
  {
    title: "Шаблоны",
    text: "структуры КП, отчётов, писем и презентаций",
    icon: "notebook-bookmark-bold-duotone",
  },
  {
    title: "Правила работы",
    text: "что можно делать сразу, а что требует подтверждения",
    icon: "shield-check-linear",
  },
] as const

const specialistSteps = [
  {
    title: "Исследование",
    detail: "собрать открытые данные, материалы компании и вводные по задаче",
    icon: "magnifer-linear",
  },
  {
    title: "Анализ",
    detail: "сопоставить факты, найти зависимости и выбрать рабочий план",
    icon: "chart-square-linear",
  },
  {
    title: "Сайт или код",
    detail: "передать техническую часть кодовому агенту или AI-инструменту",
    icon: "code-bold-duotone",
  },
  {
    title: "Проверка",
    detail: "сверить результат с задачей, доступными данными и ограничениями",
    icon: "checklist-minimalistic-linear",
  },
  {
    title: "Готовый результат",
    detail: "вернуть страницу, отчёт или список выполненных действий",
    icon: "document-add-linear",
  },
] as const

const executionSteps: readonly AtlasChatStep[] = [
  {
    title: "Понял задачу: нужна страница новой услуги и подготовка к запуску.",
    detail:
      "Сначала разделю работу на исследование, структуру страницы, техническую часть и проверку.",
    source: "analysis",
    time: "3.5 с",
  },
  {
    title: "Передаю задачу кодовому агенту.",
    detail:
      "Кодовый агент получает задачу по странице, референс, контекст компании и ограничения для запуска.",
    source: "executor",
    time: "166.7 с",
    toolCalls: [
      {
        label: "specialist",
        value: "service-page",
        icon: "programming-bold-duotone",
      },
    ],
  },
  {
    title: "Проверяю результат перед ответом.",
    detail:
      "Сверяю структуру страницы, референс, требования задачи и готовность к ручному запуску.",
    source: "review",
    time: "18.7 с",
    toolCalls: [
      {
        label: "checklist",
        value: "ready for review",
        icon: "checklist-minimalistic-linear",
      },
    ],
  },
] as const

function ServicePageAnswer() {
  return (
    <div className="sm:ml-12">
      <p className="text-sm font-semibold text-slate-950 dark:text-white">
        Черновик страницы готов к проверке.
      </p>

      <div className="mt-3 space-y-3 text-xs text-slate-700 dark:text-white/80">
        <div>
          <p className="font-semibold text-slate-950 dark:text-white">Что подготовлено</p>
          <ul className="mt-1.5 space-y-1 pl-4 text-slate-700 dark:text-white/70">
            <li className="list-disc">Структура страницы новой услуги.</li>
            <li className="list-disc">Тексты основных блоков с учётом референса.</li>
            <li className="list-disc">
              Список мест, которые нужно проверить перед публикацией.
            </li>
          </ul>
        </div>

        <div>
          <p className="font-semibold text-slate-950 dark:text-white">
            Проверка перед запуском
          </p>
          <ul className="mt-1.5 space-y-1 pl-4 text-slate-700 dark:text-white/70">
            <li className="list-disc">
              Сценарий страницы соответствует задаче и материалам компании.
            </li>
            <li className="list-disc">
              Техническая часть передана исполнителю, итог возвращён в понятном виде.
            </li>
            <li className="list-disc">Финальное решение остаётся за человеком.</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default function CompanyContextSection() {
  const sectionRef = useRef<HTMLDivElement>(null)

  useAtlasReveal(sectionRef, companyContextRevealTargets)

  return (
    <div ref={sectionRef} className="relative overflow-x-hidden">
      <section
        id="context"
        aria-labelledby="context-heading"
        className="relative bg-white py-20 dark:bg-gray-900 sm:py-28 lg:py-36"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-slate-200/70 dark:bg-white/10" />
        <div className="pointer-events-none absolute left-1/2 top-24 h-96 w-[44rem] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl dark:bg-orange-500/15" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center lg:gap-16">
            <div className="company-context-copy">
              <p className="type-eyebrow inline-flex items-center">
                <AtlasInlineOrb state="composing" className="ml-0" />
                Контекст компании
              </p>
              <h2
                id="context-heading"
                className="mt-4 max-w-4xl text-headline text-slate-950 dark:text-white"
              >
                Не объясняйте компанию каждый раз заново.
              </h2>
              <p className="mt-5 max-w-2xl text-lead text-slate-600 dark:text-slate-300">
                Atlas использует только подключённые источники и разрешённый контекст:
                CRM, задачи, события, сигналы команды и рабочие правила, которые нужны для
                конкретной задачи.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-[#f7f6f1] p-4 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-[#121418] dark:shadow-none sm:p-6">
              <div className="grid gap-4 sm:grid-cols-[minmax(0,0.92fr)_auto_minmax(0,0.92fr)] sm:items-center">
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.04]">
                  <p className="text-sm font-semibold text-slate-950 dark:text-white">
                    Подключённый контекст
                  </p>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    только источники, к которым вы дали доступ
                  </p>
                </div>
                <div className="flex items-center justify-center">
                  <AtlasNodeOrb state="composing" />
                </div>
                <div className="rounded-xl border border-slate-300 bg-slate-950 p-4 text-white dark:border-white/10">
                  <p className="text-sm font-semibold">Atlas</p>
                  <p className="mt-2 text-xs text-slate-300">
                    учитывает вводные и собирает понятный результат
                  </p>
                </div>
              </div>

              <div className="my-4 flex items-center justify-center text-slate-400">
                <Icon name="alt-arrow-down-linear" className="size-5" />
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.04]">
                <p className="type-overline text-slate-500 dark:text-white/50">
                  Новая задача
                </p>
                <p className="mt-2 text-subhead text-slate-950 dark:text-white">
                  Подготовь структуру коммерческого предложения и учти правила
                  согласования перед отправкой.
                </p>
              </div>
            </div>
          </div>

          <div className="company-context-grid mt-10 grid grid-flow-dense gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {contextItems.map((item) => (
              <article
                key={item.title}
                className="company-context-card group overflow-hidden rounded-xl border border-slate-200 bg-white p-5 transition-colors duration-300 hover:border-slate-300 dark:border-white/10 dark:bg-white/[0.035] dark:hover:border-white/20"
              >
                <div className="flex items-start gap-4">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-transform duration-700 ease-out group-hover:scale-105 dark:bg-white/10 dark:text-white">
                    <Icon name={item.icon} className="size-5" />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-950 dark:text-white">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      {item.text}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="specialists"
        aria-labelledby="specialists-heading"
        className="relative border-y border-slate-200 bg-[#f7f6f1] py-20 dark:border-white/10 dark:bg-[#101216] sm:py-28 lg:py-36"
      >
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start lg:gap-16">
            <div>
              <h2
                id="specialists-heading"
                className="max-w-4xl text-headline text-slate-950 dark:text-white"
              >
                Одна задача может требовать целой команды. Atlas соберёт её сам.
              </h2>
              <p className="mt-5 max-w-2xl text-lead text-slate-600 dark:text-slate-300">
                Для сложной работы Atlas может разделить процесс на этапы и подключить
                подходящих исполнителей: исследование, анализ, работу с сайтом или кодом,
                проверку и сборку результата.
              </p>

              <div className="specialists-flow mt-8 grid gap-3">
                {specialistSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="specialist-step group grid grid-cols-[auto_minmax(0,1fr)] gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.035]"
                  >
                    <span className="flex size-10 items-center justify-center rounded-xl bg-white text-slate-700 transition-transform duration-700 ease-out group-hover:scale-105 dark:bg-white/10 dark:text-white">
                      <Icon name={step.icon} className="size-5" />
                    </span>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="type-numeric text-xs text-brand-700 dark:text-jungle-300">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <h3 className="text-sm font-semibold text-slate-950 dark:text-white">
                          {step.title}
                        </h3>
                      </div>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        {step.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="atlas-chat-shell specialists-run overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-950 shadow-2xl shadow-slate-200/70 dark:border-white/15 dark:bg-[#16181d] dark:text-white dark:shadow-slate-950/20">
              <div className="atlas-chat-header border-b border-slate-200 bg-white px-4 py-4 dark:border-white/10 dark:bg-[#1c1f26] sm:px-6">
                <p className="text-sm font-semibold text-slate-950 dark:text-slate-200">
                  Запуск страницы новой услуги
                </p>
                <p className="mt-1 font-mono text-2xs text-slate-500 dark:text-slate-400">
                  research · code agent · review
                </p>
              </div>
              <div className="space-y-4 p-4 sm:p-6">
                <AtlasUserMessage
                  attachments={[
                    {
                      name: "landing_ref.png",
                      meta: "референс макета",
                      icon: "monitor-smartphone-bold-duotone",
                    },
                  ]}
                >
                  Добавь на сайт страницу для новой услуги и подготовь её к запуску.
                </AtlasUserMessage>
                <AtlasAgentRun
                  steps={executionSteps}
                  meta="3 шаг(а) · результат проверен · 16:29"
                />
                <ServicePageAnswer />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
